import { CommandBarButton, Stack, Text } from '@fluentui/react';
import clsx from 'clsx';
import useResizeObserver from 'use-resize-observer';
import { useTimedRefetchQuery } from '../hooks/useTimedRefetchQuery';
import { Chart } from '../types';
import { ChartEmpty, ChartEmptyProps } from './ChartEmpty';
import { ChartLoading } from './ChartLoading';
import { useStyles } from './index.styles';

export enum ChartSize {
  Small = 500,
  Medium = 750,
}

export interface ChartContainerProps<TData> {
  title: string;
  chart: Chart<TData>;
  isManualRefresh: boolean;
  showOnEmpty?: ChartEmptyProps;
  size?: ChartSize;
  timedRefreshMilliseconds: number | null;
}

const HEIGHT = 350;

export const ChartContainer = <TData,>({
  title,
  chart,
  isManualRefresh,
  showOnEmpty,
  size = ChartSize.Small,
  timedRefreshMilliseconds,
}: ChartContainerProps<TData>) => {
  const styles = useStyles();
  const {
    ref,
    width = size,
    height = HEIGHT,
  } = useResizeObserver<HTMLDivElement>();

  const { Component, hasData, query } = chart;

  const { data, loading, refetch } = useTimedRefetchQuery<TData>(
    query,
    title,
    isManualRefresh,
    timedRefreshMilliseconds
  );

  const empty = !data || !hasData(data);

  if (!loading && empty && !showOnEmpty) return null;

  return (
    <Stack grow style={{ width: size }}>
      <Stack horizontal>
        <Text
          className={clsx(
            loading ? styles.titleLoading : styles.titleReady,
            styles.chartTitle
          )}
        >
          {title}
        </Text>
        <CommandBarButton
          key="refresh"
          iconProps={{ iconName: 'refresh' }}
          onClick={() => {
            refetch();
          }}
        />
      </Stack>
      <div
        className={styles.chartContainer}
        style={{ height: HEIGHT, width: '100%' }}
        ref={ref}
      >
        {loading ? (
          <ChartLoading />
        ) : empty ? (
          <ChartEmpty {...showOnEmpty!} />
        ) : (
          <Component data={data!} height={height} width={width} />
        )}
      </div>
    </Stack>
  );
};
